import React, { useMemo } from 'react';

/**
 * TacticalGridOverlay — SVG-based tactical grid overlay for miniature combat.
 * Uses an SVG <pattern> for native browser tiling (extremely performant).
 * Renders square grid by default; architecture ready for hex grids.
 */
export function TacticalGridOverlay({
  width = 1920,
  height = 1080,
  enabled = false,
  settings = {
    size: 50,
    color: '#ffffff',
    opacity: 0.3,
    type: 'square',
    offset_x: 0,
    offset_y: 0,
  },
  className = '',
}) {
  // Unique pattern ID to avoid SVG conflicts when multiple grids exist
  const patternId = useMemo(
    () => `grid-pattern-${Math.random().toString(36).substr(2, 9)}`,
    []
  );

  if (!enabled) return null;

  const { size = 50, color = '#ffffff', opacity = 0.3, type = 'square', offset_x = 0, offset_y = 0 } = settings;

  // Clamp values for safety
  const cellSize = Math.max(10, Math.min(300, size));
  const gridOpacity = Math.max(0, Math.min(1, opacity));

  if (type === 'hex') {
    // Future: hexagonal grid pattern
    // For now, fall back to square grid
    return (
      <SquareGridSVG
        patternId={patternId}
        width={width}
        height={height}
        cellSize={cellSize}
        color={color}
        opacity={gridOpacity}
        offsetX={offset_x}
        offsetY={offset_y}
        className={className}
      />
    );
  }

  return (
    <SquareGridSVG
      patternId={patternId}
      width={width}
      height={height}
      cellSize={cellSize}
      color={color}
      opacity={gridOpacity}
      offsetX={offset_x}
      offsetY={offset_y}
      className={className}
    />
  );
}

/**
 * Square grid SVG using a repeating pattern.
 * The path draws top + left edges of each cell; tiling completes the grid.
 */
function SquareGridSVG({ patternId, width, height, cellSize, color, opacity, offsetX, offsetY, className }) {
  return (
    <svg
      className={`absolute inset-0 pointer-events-none ${className}`}
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid slice"
      data-testid="tactical-grid-overlay"
    >
      <defs>
        <pattern
          id={patternId}
          width={cellSize}
          height={cellSize}
          patternUnits="userSpaceOnUse"
          patternTransform={`translate(${offsetX}, ${offsetY})`}
        >
          {/* Draw top edge + left edge of each cell */}
          <path
            d={`M ${cellSize} 0 L 0 0 0 ${cellSize}`}
            fill="none"
            stroke={color}
            strokeWidth="1"
          />
        </pattern>
      </defs>
      <rect
        x="0"
        y="0"
        width={width}
        height={height}
        fill={`url(#${patternId})`}
        opacity={opacity}
      />
    </svg>
  );
}

export default TacticalGridOverlay;
